/**
 * The cross-host carrier for the theme choice.
 *
 * `localStorage` is partitioned by ORIGIN, and `shahedni.com`,
 * `my.shahedni.com` and `admin.shahedni.com` are three separate origins — so
 * the storage key the three frontends share never carried a choice between
 * them, whatever the comments beside it claimed. A cookie on the product apex
 * (`shahedni.com` in production, `shahedni.7ffdev.ir` on develop) is the only
 * store a browser shares between sibling origins AND exposes to a pre-paint
 * script.
 *
 * The architecture's ban on a parent-domain cookie is about the SESSION
 * cookie: it exists so an operator's privilege never rides along on an
 * anonymous public page view (project-architecture §8.1, §11). This cookie
 * carries one of two literals, `dark` or `light` — no identity, no capability
 * — and is deliberately NOT `HttpOnly`, because the bootstrap has to read it
 * from script before the first paint.
 *
 * The same three functions exist verbatim in `frontend-landing` and
 * `frontend-dashboard`. There is no shared frontend package to hold them.
 */
import type { Theme } from './tokens';

export const THEME_COOKIE_NAME = 'shahedni-theme';

/** A year. A theme choice that expires with the tab is not a preference. */
export const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * The product apexes this app is deployed on. The `Domain` attribute must name
 * one of these, or the cookie stays host-only.
 *
 * Why not "the last two labels"? That rule is only correct while the product
 * apex is itself two labels. The develop hosts are `*.shahedni.7ffdev.ir`, so
 * last-two-labels yields `7ffdev.ir` — the apex that EVERY product on the same
 * infrastructure shares (`qanatek.7ffdev.ir`, `tizvir.7ffdev.ir`, …). A theme
 * cookie scoped there is sent to all of them. Naming the product apex
 * explicitly is the only rule that cannot collapse onto a sibling product's
 * hosts, and it happens to leave the two-label production apex (`shahedni.com`)
 * unchanged.
 */
const THEME_COOKIE_APEXES = ['shahedni.com', 'shahedni.7ffdev.ir'] as const;

/**
 * The `Domain` attribute for a hostname, or `''` when the cookie must stay
 * host-only.
 *
 * A host matches a known product apex when it IS that apex (`shahedni.com`) or
 * ends with `.` + that apex (`my.shahedni.7ffdev.ir`): naming the apex is what
 * makes the cookie a DOMAIN cookie, so it reaches `my.` and `admin.`. A
 * host-only cookie set on the apex is NOT sent to its own subdomains.
 * `localhost` and a bare IP take no `Domain` — a browser rejects it on both.
 *
 * An UNRECOGNISED host falls back to `''` (host-only), deliberately, and not to
 * a guessed parent. A host we have not reasoned about is a host whose parent is
 * unknown; returning the wrong parent leaks this cookie to whoever owns it,
 * whereas a host-only cookie can only ever be read by the origin that set it.
 * The safe failure is the correct failure here.
 */
export function themeCookieDomain(hostname: string): string {
  if (!hostname) return '';

  const host = hostname.toLowerCase();
  if (host === 'localhost') return '';
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(host)) return '';
  if (host.includes(':')) return '';

  for (const apex of THEME_COOKIE_APEXES) {
    if (host === apex || host.endsWith(`.${apex}`)) return apex;
  }

  return '';
}

/** Serialises the cookie value the toggle writes. */
export function serialiseThemeCookie(theme: Theme, hostname: string, isSecure: boolean): string {
  const domain = themeCookieDomain(hostname);

  return [
    `${THEME_COOKIE_NAME}=${theme}`,
    'Path=/',
    `Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    domain ? `Domain=${domain}` : '',
    isSecure ? 'Secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

/**
 * Reads the carried choice, or `null` when there is none to carry. Wrapped
 * because `document.cookie` itself throws in a partition that refuses cookies.
 */
export function readThemeCookie(): Theme | null {
  try {
    const matched = document.cookie.match(/(?:^|; )shahedni-theme=([^;]*)/);
    if (!matched?.[1]) return null;

    const value = decodeURIComponent(matched[1]);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}
